import { Carousel } from '@liveauctioneers/hammer-ui-core/carousel';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useMemo } from 'react';
import isEqual from 'react-fast-compare';

import { useAppSelector } from '@/redux/hooks';
import { getCatalogsForFollowedSellers } from '@/redux/modules/filteredItems';
import { getFollowedSellerIds } from '@/redux/modules/followSeller';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getAuctionCalendarUrlWithParams } from '@/utils/urls';

import CarouselLoadingWrapper from '../CarouselLoadingWrapper/CarouselLoadingWrapper';
import { CatalogCard } from '../CatalogCard/CatalogCard';
import { HomepageAuctionCard } from '../CatalogCard/HomepageAuctionCard/HomepageAuctionCard';

type Props = {
    className?: string;
    shouldUseHomePageRedesign?: boolean;
};

export const AuctionsYouMayLikeCarousel = ({ className, shouldUseHomePageRedesign = false }: Props) => {
    const formatMessage = useAppFormatMessage();
    const followedSellerIds = useAppSelector(getFollowedSellerIds, isEqual);
    const catalogIds = useAppSelector(getCatalogsForFollowedSellers);

    const catalogCards = useMemo(
        () =>
            catalogIds.map((catalogId, index) => ({
                card: (
                    <div
                        className={cn(
                            'focus:outline-hidden',
                            shouldUseHomePageRedesign
                                ? 'w-[381px] xsMax:w-[280px]'
                                : 'w-[285px] [&_.featuredCardClass]:block [&_.featuredCardClass]:max-w-full'
                        )}
                        key={`${catalogId}-youmay-like`}
                    >
                        {shouldUseHomePageRedesign ? (
                            <HomepageAuctionCard
                                catalogId={catalogId}
                                lazyLoadImages
                                position={index + 1}
                            />
                        ) : (
                            <CatalogCard catalogId={catalogId} />
                        )}
                    </div>
                ),
                key: `${catalogId}-youmay-like`,
            })),
        [catalogIds, shouldUseHomePageRedesign]
    );

    if (catalogIds.length < 4) {
        return null;
    }

    return (
        <div className={cn('flex flex-col', className)}>
            <div
                className={cn(
                    'relative w-full self-center p-0',
                    shouldUseHomePageRedesign ? 'max-w-full text-left' : 'max-w-[1240px] text-center'
                )}
            >
                <CarouselLoadingWrapper>
                    <Carousel
                        action={{
                            path: getAuctionCalendarUrlWithParams({
                                house_id: followedSellerIds,
                            }),
                            text: String(formatMessage(['viewAll'])),
                        }}
                        cards={catalogCards}
                        useOverlayControls
                        title={String(
                            formatMessage([
                                shouldUseHomePageRedesign ? 'newFromFollowedAuctioneers' : 'auctions_you_may_like',
                            ])
                        )}
                    />
                </CarouselLoadingWrapper>
            </div>
        </div>
    );
};
